import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { POPOUT_PLAYER_TARGET } from "shared/constants";
import { type Nullable } from "shared/types";
import { type RadioClip, type TvChannelClip } from "shared/types/mentions";

export type UniversalPlayerClip =
  | TvChannelClip
  | RadioClip
  | Pick<
      RadioClip,
      "end_time" | "id" | "media_url" | "source" | "start_time" | "type"
    >;

export type UniversalPlayerTarget =
  | typeof POPOUT_PLAYER_TARGET
  | `tv-card-${string}`
  | `radio-card-${string}`
  | `podcast-card-${string}`;

export interface PlayerPopout {
  playNow: (clip: UniversalPlayerClip) => void;
}

export const useUniversalPlayerStore = defineStore("universalPlayer", () => {
  const activeControl = ref<Nullable<string>>(null);
  const addedClip = ref<Nullable<UniversalPlayerClip>>(null);
  const playerClip = ref<UniversalPlayerClip>();
  const playerInitialClip = ref<Nullable<UniversalPlayerClip>>(null);
  const playerIsBeingScrubbed = ref<boolean>(false);
  const playerIsDismissed = ref<boolean>(false);
  const playerIsExpanded = ref<boolean>(false);
  const playerIsPlaying = ref<boolean>(false);
  const playerCurrentTime = ref<number>(0);
  const playerList = ref<UniversalPlayerClip[]>([]);
  const playerOpen = ref<boolean>(false);

  const playerPopoutRef = ref<Nullable<PlayerPopout>>(null);

  const playerPopoutTarget = ref<UniversalPlayerTarget>(POPOUT_PLAYER_TARGET);

  const popoutVideoRef = ref<Nullable<HTMLVideoElement>>(null);
  const universalPlayerRef = ref<Nullable<HTMLVideoElement>>(null);

  const getPlayerOpenStyle = computed(() =>
    playerOpen.value ? { marginBottom: "56px" } : {}
  );

  const isPopoutPoppingOut = computed(() => {
    if (!playerInitialClip.value) return false;

    return playerPopoutTarget.value === POPOUT_PLAYER_TARGET;
  });

  const isPlayerBeingScrubbed = computed(() => playerIsBeingScrubbed.value);

  const playerCurrentTimestamp = computed(() => {
    if (!playerClip.value?.start_time) return 0;

    return Math.trunc(playerClip.value.start_time + playerCurrentTime.value);
  });

  function playerHide() {
    playerOpen.value = false;
  }

  function playerShow() {
    playerOpen.value = true;
  }

  function playerSetInitialClip(clip: UniversalPlayerClip) {
    playerClip.value = clip;
    playerInitialClip.value = clip;
  }

  function playerUpdateClip(clip: UniversalPlayerClip) {
    playerClip.value = clip;
  }

  function playerSetRef(playerRef: Nullable<HTMLVideoElement>) {
    universalPlayerRef.value = playerRef;
  }

  function setPlayerIsBeingScrubbed(isScrubbing: boolean) {
    playerIsBeingScrubbed.value = isScrubbing;
  }

  function setPlayerCurrentTime(currentTime: number) {
    playerCurrentTime.value = currentTime;
  }

  function setPlayerPopoutRef(popoutRef: Nullable<PlayerPopout>) {
    playerPopoutRef.value = popoutRef;
  }

  function setPopoutVideoRef(videoRef: Nullable<HTMLVideoElement>) {
    popoutVideoRef.value = videoRef;
  }

  function playerInsertClip(clip: UniversalPlayerClip) {
    if (playerList.value.every((listItem) => listItem.id !== clip.id)) {
      const listIndex = playerList.value.findIndex(
        (listItem) => listItem.id === playerClip.value?.id
      );

      playerList.value.splice(listIndex + 1, 0, clip);
      addedClip.value = clip;
    }
  }

  function playerAppendClip(clip: UniversalPlayerClip) {
    if (playerList.value.every((listItem) => listItem.id !== clip.id)) {
      playerList.value.push(clip);
      addedClip.value = clip;
    }
  }

  function playerClearList() {
    playerList.value = [];
  }

  function playerRemoveListItem(clip: UniversalPlayerClip) {
    playerList.value = playerList.value.filter(
      (listItem) => listItem.id !== clip.id
    );
  }

  function setPlayerPlaying(playing: boolean) {
    playerIsPlaying.value = playing;
  }

  function setPlayerPopoutTarget(target: UniversalPlayerTarget) {
    playerPopoutTarget.value = target;
  }

  function setPlayerPopoutActiveControl(control: Nullable<string>) {
    activeControl.value = control;
  }

  function setPlayerDismissed(dismissed: boolean) {
    playerIsDismissed.value = dismissed;
  }

  function setPlayerExpanded(expanded: boolean) {
    playerIsExpanded.value = expanded;
  }

  function closeAndStop() {
    playerHide();

    if (universalPlayerRef.value) {
      universalPlayerRef.value.pause();
    }
  }

  return {
    activeControl,
    addedClip,
    closeAndStop,
    getPlayerOpenStyle,
    isPlayerBeingScrubbed,
    isPopoutPoppingOut,
    playerAppendClip,
    playerClearList,
    playerClip,
    playerCurrentTime,
    playerCurrentTimestamp,
    playerHide,
    playerInitialClip,
    playerInsertClip,
    playerIsBeingScrubbed,
    playerIsDismissed,

    playerIsExpanded,
    playerIsPlaying,
    playerList,

    playerOpen,
    playerPopoutRef,
    playerPopoutTarget,
    playerRemoveListItem,
    playerSetInitialClip,
    playerSetRef,
    playerShow,
    playerUpdateClip,
    popoutVideoRef,
    setPlayerCurrentTime,
    setPlayerDismissed,
    setPlayerExpanded,
    setPlayerIsBeingScrubbed,
    setPlayerPlaying,
    setPlayerPopoutActiveControl,
    setPlayerPopoutRef,
    setPlayerPopoutTarget,
    setPopoutVideoRef,
    universalPlayerRef,
  };
});

export default useUniversalPlayerStore;
